import React, {
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Accordion from 'core/components/Accordion';
import {
  MissingClaimInfo,
} from 'dtos';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import {
  useFormContext,
} from 'react-hook-form';

type FormValues = {
  correctedPayerName: string;
  claimInputs: Record<string, string>;
};

type Props = {
  unmatchedClaims: MissingClaimInfo[];
};

type UnmatchedClaimCardProps = {
  claim: MissingClaimInfo;
};

function UnmatchedClaimCard({
  claim,
}: UnmatchedClaimCardProps) {
  const { control } = useFormContext<FormValues>();
  const hasExtracted = !isNil(claim.claimExternalId) && !isEmpty(claim.claimExternalId);

  return (
    <div className="rounded-lg border border-orange-100 bg-white p-2.5 flex flex-col gap-2 !bg-[#FAEEDA22]">
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <ErrorOutlineOutlinedIcon className="!h-4 !w-4 !text-orange-900 shrink-0" />
            <span className="text-xs font-semibold text-gray-800">{claim.patientName ?? '—'}</span>
          </div>
          {!isNil(claim.dateOfService) && !isEmpty(claim.dateOfService) && (
          <span className="text-xs text-gray-600">
            Visit
              {' '}
            {claim.dateOfService}
          </span>
          )}
        </div>
      </div>
      <div className="reconcile-dialog-field-row">
        <Input
          id={`ai-extracted-claim-${claim.claimExternalId}`}
          label="AI Extracted"
          value={hasExtracted ? (claim.claimExternalId ?? '') : ''}
          disabled
          className="reconcile-dialog-input"
        />
        <span className="pb-1.5 text-xs text-gray-300">→</span>
        <Controller
          control={control}
          name={`claimInputs.${claim.claimExternalId ?? ''}`}
          render={({ field }) => (
            <Input
              {...field}
              onChange={(val) => { field.onChange(val); }}
              label="Correct Claim #"
              placeholder="Enter correct claim number"
              className="reconcile-dialog-input"
            />
          )}
        />
      </div>
    </div>
  );
}

export default function UnmatchedClaimsSection({
  unmatchedClaims,
}: Props) {
  const [expanded, setExpanded] = useState(true);
  const Icon = expanded ? KeyboardArrowDownIcon : KeyboardArrowRightIcon;

  return unmatchedClaims.length > 0 ? (
    <Accordion
      expanded={expanded}
      onChange={(_, nextExpanded) => { setExpanded(nextExpanded); }}
      className="!bg-transparent !shadow-none !border-0 !before:hidden !my-0"
      expandIcon={<span className="hidden" />}
      header={(
        <div className="flex items-center gap-0.5 text-[13px] font-semibold text-gray-800">
          <Icon className="!h-4 !w-4" />
          <span>Unmatched Claims (</span>
          <span>{unmatchedClaims.length}</span>
          <span>)</span>
        </div>
      )}
      headerClasses={{
        root: '!min-h-0 !h-6 !px-0 !py-0',
        content: '!m-0',
        expandIconWrapper: '!hidden',
      }}
      detailsClasses={{ root: '!p-0' }}
    >
      <div className="flex flex-col gap-1.5">
        {unmatchedClaims.map((claim) => (
          <UnmatchedClaimCard
            key={claim.claimExternalId ?? claim.patientName}
            claim={claim}
          />
        ))}
      </div>
    </Accordion>
  ) : null;
}
